import { evolve, find, map, pipe, propEq, values as ramdaValues } from 'ramda'
import toUpper from 'lodash/toUpper'

import { renameKeys } from '@/utils'
import toArray from '@/utils/to-array'

type AsKeys<T> = Record<string, T>

type AsArray<T> = T[]

type RawBase = {
  txt_id: string
  name: string
}

type FormattedBase = {
  value: string
  label: string
}

type AsRaw<T> = Omit<T, keyof FormattedBase> & RawBase
type AsFormatted<T> = Omit<T, keyof RawBase> & FormattedBase

type State = {
  txt_id: string
  name: string
}

export type Country = {
  txt_id: string
  name: string
  capital: string
  Continent: string
  top_level_domain: string
  calling_code: string
  currency_code: string
  currency_name: string
  states?: AsKeys<State>
}

const toFormatted = <T extends RawBase>(value: T): AsFormatted<T> =>
  renameKeys({ txt_id: 'value', name: 'label' }, value) as AsFormatted<T>

export const getFormattedCountries = pipe<
  AsKeys<AsRaw<Country>>,
  AsArray<AsRaw<Country>>,
  AsArray<AsFormatted<Country>>
>(ramdaValues, map<AsRaw<Country>, AsFormatted<Country>>(toFormatted))

const formatStates = pipe<
  AsKeys<AsRaw<State>>,
  AsArray<AsRaw<State>>,
  AsArray<AsFormatted<State>>
>(
  ramdaValues,
  map<AsRaw<State>, AsFormatted<State>>(
    pipe<AsRaw<State>, AsFormatted<State>, AsFormatted<State>>(
      toFormatted,
      evolve({ value: toUpper })
    )
  )
)

export const getCountry = (
  countryCode: string,
  countries: AsKeys<AsRaw<Country>>
): AsRaw<Country> | undefined =>
  find<AsRaw<Country>>(
    propEq('txt_id', countryCode.toLowerCase()),
    toArray(countries)
  )

export const getState = (
  stateCode: string,
  states: AsKeys<AsRaw<State>>
): AsRaw<State> | undefined =>
  find<AsRaw<State>>(propEq('txt_id', stateCode.toUpperCase()), toArray(states))

export const getFormattedStates = (
  countries: AsKeys<AsRaw<Country>>,
  countryCode: string
): AsArray<AsFormatted<State>> => {
  const countryData = getCountry(countryCode, countries)
  if (!countryData?.states) return []
  return formatStates(countryData.states)
}
